import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Pause, Play, Square } from '@phosphor-icons/react';
import { Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import Timer from './Timer';
import type { RecorderState } from '../../store';
import AudioWaveform from './AudioWaveform';
import ControlButtons from './ControlButtons';
import AudioStatusBadge, { type AudioStatus } from './AudioStatusBadge';
import CameraPreview from '../MediaPreview/Camera';
import Display from '../MediaPreview/Display';
import { streams } from '../utils/streamUtils';
import {
  stopRecordingThunk,
  pauseRecordingThunk,
  resumeRecordingThunk,
} from '../../thunk';

interface RecorderControlsProps {
  onToggleNotes: () => void;
  showNotes: boolean;
  isPopupMode?: boolean;
  audioStatus?: AudioStatus | null;
}

const RecorderControls: React.FC<RecorderControlsProps> = ({
  onToggleNotes,
  showNotes,
  isPopupMode = false,
  audioStatus = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { status, selectedDevices, settings } = useSelector(
    (state: RecorderState) => state.recorder,
  );
  const isRecording = status === 'recording';
  const isPaused = status === 'paused';
  const isStopped = status === 'stopped';
  const isSaving = status === 'saving';
  const showTimer = isRecording || isPaused;
  const showControls = isRecording || isPaused;
  const showLoader = isSaving;
  const isControlDisabled = !['recording', 'paused'].includes(status);

  const stopRecordingHandler = () => {
    dispatch(stopRecordingThunk());
  };

  const togglePauseResumeHandler = () => {
    if (isPaused) {
      dispatch(resumeRecordingThunk());
    } else if (isRecording) {
      dispatch(pauseRecordingThunk());
    }
  };

  const isSmall = typeof window !== 'undefined' && window.innerWidth < 640;

  // In popup mode, we don't show the camera preview
  const showCameraPreview =
    (status === 'recording' || status === 'paused') && !isSmall && !isPopupMode;

  // In popup mode, we show a smaller display preview
  const showDisplayPreview =
    (status === 'recording' || status === 'paused') && !isPopupMode;

  // Check if display media stream has video tracks
  const hasDisplayVideoTracks =
    streams.displayMediaStream?.getVideoTracks().length > 0 &&
    !settings.audioSettings.isTabAudioOnly;

  // Check if user media stream has video tracks
  const hasUserVideoTracks =
    streams.userMediaStream?.getVideoTracks().length > 0;

  // Determine if we should show full-sized camera when display is not shared
  const showFullSizeCamera =
    showDisplayPreview &&
    !hasDisplayVideoTracks &&
    hasUserVideoTracks &&
    selectedDevices.videoinput.deviceId !== 'none';

  return (
    <div
      className={cn(
        'flex flex-col bg-background w-full',
        !isPopupMode && 'mb-2',
        showLoader && (isPopupMode ? 'h-auto' : 'h-screen'),
        showLoader && 'items-center justify-center',
      )}
    >
      {!showLoader ? (
        <>
          {showDisplayPreview && (
            <div className="relative mb-4 w-full">
              <div className="relative w-full">
                {(() => {
                  if (hasDisplayVideoTracks) {
                    return (
                      <>
                        <Display />
                        {showCameraPreview && (
                          <div className="absolute bottom-2 left-2 w-24 h-24 z-10">
                            <CameraPreview />
                          </div>
                        )}
                      </>
                    );
                  }

                  if (showFullSizeCamera) {
                    return (
                      <div className="w-full rounded-xl overflow-hidden mb-2 shadow-md">
                        <CameraPreview fullSize />
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          <div
            className={cn(
              'flex items-center gap-2 bg-card rounded-full px-2 py-1 border border-input transition-opacity duration-300',
              isStopped ? 'opacity-0 invisible' : 'opacity-100 visible',
            )}
          >
            {/* Left Controls */}
            {showControls && (
              <ControlButtons
                onToggleNotes={isPopupMode ? () => {} : onToggleNotes}
                showNotes={isPopupMode ? false : showNotes}
                isPopupMode={isPopupMode}
              />
            )}

            {/* Inline Audio Status Badge */}
            {audioStatus && showControls && (
              <>
                <div className="w-px h-5 bg-border opacity-80 flex-shrink-0" />
                <div className="flex-shrink-0">
                  <AudioStatusBadge status={audioStatus} />
                </div>
              </>
            )}

            <div className="w-px h-5 bg-border opacity-80 flex-shrink-0" />

            {/* Center: Audio Waveform */}
            <div className="flex-1 min-w-0">
              <div className="w-full h-8">
                <div
                  className="relative h-full w-full"
                  aria-label="Audio waveform display"
                >
                  <div
                    className={cn(
                      'h-full w-full transition-opacity',
                      isRecording ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    <AudioWaveform />
                  </div>
                  <div
                    className={cn(
                      'absolute top-0 left-0 w-full h-full flex items-center justify-center transition-opacity text-sm text-muted-foreground',
                      isPaused || isStopped ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    {isPaused
                      ? t('recorderControls.status.paused', 'Paused')
                      : t('recorderControls.status.saving', 'Saving...')}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-px h-5 bg-border opacity-80 flex-shrink-0" />

            {/* Timer */}
            {showTimer && (
              <div
                className={cn(
                  'flex items-center px-1 flex-shrink-0',
                  isPopupMode ? 'text-xs' : 'text-lg',
                )}
              >
                <h6 className="font-normal text-foreground">
                  <Timer />
                </h6>
              </div>
            )}

            <div className="w-px h-5 bg-border opacity-80 flex-shrink-0" />

            {/* Pause/Resume Button */}
            {showControls && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={togglePauseResumeHandler}
                      disabled={isControlDisabled}
                      className={cn(
                        'flex items-center justify-center w-10 h-10 transition-opacity duration-200',
                        isControlDisabled
                          ? 'cursor-default opacity-50'
                          : 'cursor-pointer hover:[&>svg]:scale-110',
                      )}
                    >
                      {isPaused ? (
                        <Play
                          className="h-5 w-5 text-primary transition-transform duration-200"
                          weight="fill"
                        />
                      ) : (
                        <Pause
                          className="h-5 w-5 text-foreground transition-transform duration-200"
                          weight="fill"
                        />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isPaused
                      ? t('recorderControls.tooltips.resume', 'Resume')
                      : t('recorderControls.tooltips.pause', 'Pause')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Stop Button */}
            {showControls && (
              <TooltipProvider>
                <div
                  className={cn(
                    'relative inline-flex items-center justify-center bg-destructive rounded-full h-8 w-8 transition-all duration-300 ease-out flex-shrink-0',
                    isControlDisabled
                      ? 'cursor-default opacity-50'
                      : 'cursor-pointer hover:bg-destructive/90',
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        onClick={stopRecordingHandler}
                        disabled={isControlDisabled}
                        id="stop-recording-button"
                        className="text-white h-full w-full min-w-0 rounded-full transition-opacity duration-200 hover:bg-transparent hover:text-white disabled:text-white disabled:opacity-90 focus-visible:ring-0 flex items-center justify-center p-0"
                        title="Stop recording"
                      >
                        <Square
                          className="h-3.5 w-3.5 text-white"
                          weight="fill"
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(
                        'recorderControls.tooltips.stopRecording',
                        'Stop Recording',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            )}
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center h-8">
          <Loader className="h-8 w-8 animate-spin text-muted-foreground/50" />
        </div>
      )}
    </div>
  );
};

export default RecorderControls;
